import { FC, useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PageDescription } from '@/components/page-description';
import { useOrgStore } from '@/stores/org-store';
import { useNotification } from '@/components/notifications';
import { deleteLabel } from '@/services/workflow/delete-label';
import {
  useGetOrgLabels,
  ORG_LABELS_QUERY_KEY,
} from '@/hooks/queries/use-get-org-labels';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pencil, Plus, Search, Tag, Trash2 } from 'lucide-react';
import { CreateLabelModal } from '@/components/create-label-modal';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { Label } from '@/types/workflow-types';
import * as Sentry from '@sentry/react';

export const LabelsPage: FC = () => {
  const { currentOrg } = useOrgStore();
  const notification = useNotification();
  const queryClient = useQueryClient();

  const { data: labels = [], isLoading } = useGetOrgLabels();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingLabel, setEditingLabel] = useState<Label | null>(null);
  const [query, setQuery] = useState('');

  const [deletingLabel, setDeletingLabel] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const openCreate = useCallback(() => {
    setEditingLabel(null);
    setModalOpen(true);
  }, []);

  const handleEdit = useCallback((label: Label) => {
    setEditingLabel(label);
    setModalOpen(true);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!currentOrg?.orgid || !deletingLabel) return;

    setIsDeleting(true);
    try {
      await deleteLabel(currentOrg.orgid, deletingLabel.id);
      queryClient.invalidateQueries({ queryKey: [ORG_LABELS_QUERY_KEY] });
      notification.success(`Label "${deletingLabel.name}" deleted`);
      setDeletingLabel(null);
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'delete-label', labelId: deletingLabel.id },
      });
      notification.error('Failed to delete label');
    } finally {
      setIsDeleting(false);
    }
  }, [currentOrg?.orgid, deletingLabel, notification, queryClient]);

  const filteredLabels = useMemo(() => {
    const q = query.trim().toLowerCase();
    return labels
      .filter(
        (l) =>
          !q ||
          l.name.toLowerCase().includes(q) ||
          (l.description ?? '').toLowerCase().includes(q),
      )
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [labels, query]);

  const tableContent = useMemo(() => {
    if (isLoading) {
      return (
        <div className='flex items-center justify-center py-12'>
          <div className='size-6 border-2 border-primary border-t-transparent rounded-full animate-spin' />
        </div>
      );
    }

    if (labels.length === 0) {
      return (
        <div className='flex flex-col items-center justify-center rounded-xl border border-dashed py-12'>
          <Tag className='size-8 text-muted-foreground mb-3' />
          <p className='text-muted-foreground mb-4'>
            No labels configured for this organization.
          </p>
          <Button onClick={openCreate}>
            <Plus className='size-4' />
            Create Label
          </Button>
        </div>
      );
    }

    return (
      <div className='overflow-hidden rounded-xl border bg-card shadow-sm'>
        <table className='w-full table-fixed text-sm'>
          <thead>
            <tr className='border-b bg-muted/50'>
              <th className='h-10 px-4 text-left font-medium text-muted-foreground'>
                Label
              </th>
              <th className='h-10 px-2 text-left font-medium text-muted-foreground'>
                Description
              </th>
              <th className='h-10 w-[110px] px-4 text-right font-medium text-muted-foreground'>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredLabels.map((label) => {
              const color = label.color || '#71717a';
              return (
                <tr
                  key={label.id}
                  className='border-b last:border-b-0 hover:bg-muted/30 transition-colors'
                >
                  <td className='py-2 pl-4 pr-2 align-middle'>
                    <span
                      className='inline-flex items-center rounded-lg border px-2.5 py-1 text-sm font-medium text-foreground whitespace-nowrap'
                      style={{
                        borderColor: `${color}8c`,
                        backgroundColor: `${color}14`,
                      }}
                    >
                      {label.name}
                    </span>
                  </td>
                  <td
                    className={`px-2 align-middle truncate ${
                      label.description
                        ? 'text-foreground'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {label.description || '—'}
                  </td>
                  <td className='py-1.5 pl-2 pr-4 align-middle'>
                    <div className='flex justify-end gap-0.5'>
                      <Button
                        variant='ghost'
                        size='sm'
                        className='size-8 p-0'
                        title='Edit label'
                        onClick={() => handleEdit(label)}
                      >
                        <Pencil className='size-4' />
                      </Button>
                      <Button
                        variant='ghost'
                        size='sm'
                        className='size-8 p-0 hover:bg-destructive/10 hover:text-destructive'
                        title='Delete label'
                        onClick={() =>
                          setDeletingLabel({ id: label.id, name: label.name })
                        }
                      >
                        <Trash2 className='size-4' />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredLabels.length === 0 && (
          <div className='px-6 py-12 text-center text-sm text-muted-foreground'>
            No labels match your search.
          </div>
        )}
      </div>
    );
  }, [isLoading, labels, filteredLabels, handleEdit, openCreate]);

  return (
    <div className='w-full'>
      <div className='flex flex-col sm:flex-row justify-between items-start gap-4 mb-5'>
        <div className='min-w-0'>
          <div className='flex items-center gap-2'>
            <h1 className='text-2xl font-semibold tracking-tight'>Labels</h1>
            <span className='inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium text-muted-foreground'>
              {labels.length}
            </span>
          </div>
          <PageDescription>
            Create and configure labels for your workflows. Labels help organize
            and categorize workflows across your organization.
          </PageDescription>
        </div>
        <Button className='shrink-0' onClick={openCreate}>
          <Plus className='size-4' />
          Create Label
        </Button>
      </div>

      {labels.length > 0 && (
        <div className='relative mb-5 w-full max-w-80'>
          <Search className='absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground pointer-events-none' />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Search labels'
            className='pl-9'
          />
        </div>
      )}

      <CreateLabelModal
        isOpen={modalOpen}
        onOpenChange={setModalOpen}
        editingLabel={editingLabel}
      />
      <DeleteConfirmationModal
        isOpen={!!deletingLabel}
        title='Delete Label'
        description={`Are you sure you want to delete "${deletingLabel?.name ?? ''}"? This will remove it from all workflows. This action cannot be undone.`}
        buttonLabel='Delete Label'
        isLoading={isDeleting}
        handleDelete={handleDelete}
        handleCancel={() => setDeletingLabel(null)}
      />
      {tableContent}
    </div>
  );
};
